import React, { useState, useRef } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

import DashboardSidebar from "../DashboardSidebar";
import { useParams, useLocation, useNavigate  } from "react-router-dom";
import AddProductsDialog from "./AddProductsDialog";
import "./ProductGroups.css";
import useFetch from "../../hooks/useFetch";
import { Box, Checkbox, Typography } from "@mui/material";

const SortableProductRow: React.FC<{
    id: string | number;
    children: (dragHandle: React.ReactNode) => React.ReactNode;
  }> = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: String(id) });
  const dragHandle = (
    <span
      {...attributes}
      {...listeners}
      style={{
      cursor: "grab",
      padding: "0 6px",
      color: "#aaa",
      display: "flex",
      alignItems: "center",
      alignSelf: "center",
      flexShrink: 0,
      height: "100%",
    }}
    >
      <DragIndicatorIcon fontSize="small" />
    </span>
  );
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
    >
      {children(dragHandle)}
    </div>
  );
};


const EditCollections: React.FC = () => {

  const location = useLocation();
  const navigate = useNavigate();

  const incomingTab = location.state?.tab ?? "edit";
  const isProductsMode = incomingTab === "products";

  const incomingCollection = location.state?.collection ?? null;
  const isEditMode = !!incomingCollection;

  const [collectionName, setCollectionName] = useState(incomingCollection?.title ?? "");
  const [description, setDescription] = useState(incomingCollection?.description ?? "");
  const [saveMsg, setSaveMsg] = useState("");
  const [savedCollectionId, setSavedCollectionId] = useState<number | null>(incomingCollection?.id ?? null);
  const [addProductsOpen, setAddProductsOpen] = useState(false);
  const [, setRemovedProductIds] = useState<number[]>([]);


  const [hasReordered, setHasReordered] = useState(false);
  const originalProductsRef = useRef<any[]>([]);
  const sensors = useSensors(useSensor(PointerSensor));

  const { store_id } = useParams<{ store_id: string }>();
  const userStoreId = store_id || location.state?.collection?.store_id || location.state?.storeId;

  const { fetchData: updateCollection, isLoading: isUpdating, errorContent: updateErrorContent } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/productgroups/update/${savedCollectionId}`,
    method: "PUT",
  });

  const { fetchData: createCollection, isLoading: isCreating, errorContent: createErrorContent } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/productgroups/add`,
    method: "POST",
  });

  const isSaving = isUpdating || isCreating;

  const { data: productsData, fetchData: fetchProducts, isLoading: productsLoading, errorContent: productsErrorContent } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/productgroups/${savedCollectionId}/products`,
    method: "GET",
  });

  const { fetchData: updateProductColors, errorContent: _colorErrorContent } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/productgroups/${savedCollectionId}/products`,
    method: "POST",
  });

  const { fetchData: updateSortOrder, isLoading: isSavingOrder, errorContent: _sortErrorContent } = useFetch({
    url: `${import.meta.env.VITE_API_URL}/productgroups/${savedCollectionId}/sort-order`,
    method: "PUT",
  });

  const [collectionProducts, setCollectionProducts] = React.useState<any[]>([]);
  const [productColorChanges, setProductColorChanges] = useState<Record<string, string[]>>({});
  const [productToastMsg, setProductToastMsg] = useState<Record<string, "success" | "error">>({});
  const [sortOrderToast, setSortOrderToast] = useState<"success" | "error" | null>(null);
  const [productsActionError, setProductsActionError] = useState<React.ReactNode>(null);
  const [removingProductId, setRemovingProductId] = useState<number | null>(null);

  React.useEffect(() => {
    if (!isProductsMode || !savedCollectionId) return;
    fetchProducts();
  }, [isProductsMode, savedCollectionId]);

  React.useEffect(() => {
    if (productsData?.products) setCollectionProducts(productsData.products);
  }, [productsData]);

  const handleSave = async () => {
    if (!collectionName.trim()) { setSaveMsg("Collection name is required."); return; }
    setSaveMsg("");

    if (isEditMode && savedCollectionId) {
      const result = await updateCollection({ 
        store_id: Number(userStoreId), 
        title: collectionName, 
        description: description || null 
      });
      if (result?.success) {
        setRemovedProductIds([]);
        navigate(`/product-groups/${userStoreId}`, {
          state: { successMsg: "Collection updated successfully!" }
        });
      } else {
        setSaveMsg("");
        // setSaveMsg((result?.message || "Update failed."));
      }
    } else {
      if (savedCollectionId) { 
        navigate(`/collections/${userStoreId}`, {
          state: { successMsg: "Product Group saved successfully!" }
        }); 
        return; 
      }
      const result = await createCollection({ 
        store_id: Number(userStoreId), 
        title: collectionName, 
        description: description || null, 
        status: "1" 
      });
      if (result?.success) {
        setSavedCollectionId(result.data.storeProductGroup.id);
        setSaveMsg("Product Group saved! You can now add products.");
      } else {
        setSaveMsg("");
        // setSaveMsg((result?.message || "Save failed."));
      }
    }
  };
  const handleRemoveProduct = async (productId: number) => {
    const confirmed = window.confirm("Are you sure you want to remove this product?");
    if (!confirmed) return;
    setRemovingProductId(productId);
    const token = localStorage.getItem("token");
    const res = await fetch(`${import.meta.env.VITE_API_URL}/productgroups/${savedCollectionId}/products/${productId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    const result = await res.json();
    if (result?.success) {
      setCollectionProducts(prev => prev.filter(p => p.id !== productId));
      setRemovingProductId(null);
    } else {
      const isAuthError = result?.code === 401 || result?.message?.toLowerCase().includes("token");
      const msg = isAuthError
        ? <>⚠️ Session expired. Please <a href="/login" className="login-error-showup">login</a> again.</>
        : (result?.message || "Failed to remove product.");
      if (!isAuthError) window.alert(result?.message || "Failed to remove product.");
      setProductsActionError(msg);
      setRemovingProductId(null);
    }
  };

  const handleCancelColorChanges = (productId: string) => {
    setProductColorChanges(prev => {
      const next = { ...prev };
      delete next[productId];
      return next;
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setCollectionProducts(prev => {
      if (!hasReordered) {
        originalProductsRef.current = [...prev]; // 📸 snapshot before first drag
      }
      const oldIndex = prev.findIndex(p => String(p.id) === String(active.id));
      const newIndex = prev.findIndex(p => String(p.id) === String(over.id));
      return arrayMove(prev, oldIndex, newIndex);
    });
    setHasReordered(true);
  };

  const handleSaveOrder = async () => {
    const result = await updateSortOrder({
      products: collectionProducts.map((p, i) => ({
        product_id: p.id,
        sort_order: i + 1,
      })),
    });
    if (result?.success) {
      originalProductsRef.current = [];
      setHasReordered(false);
      setSortOrderToast("success");
      setTimeout(() => setSortOrderToast(null), 2500);
    } else {
      setSortOrderToast("error");
      setTimeout(() => setSortOrderToast(null), 2500);
      const msg = result?.code === 401 || result?.message?.toLowerCase().includes("token")
        ? <>⚠️ Session expired. Please <a href="/login" style={{ color: "#a59cff", textDecoration: "underline" }}>login</a> again.</>
        : (result?.message || "Failed to save order.");
      setProductsActionError(msg);
    }
  };

  const handleCancelOrder = () => {
    setCollectionProducts([...originalProductsRef.current]); // revert locally
    originalProductsRef.current = [];
    setHasReordered(false);
  };

  const handleUpdateProductColors = async (product: any) => {
    const selectedVariants = productColorChanges[product.shopify_product_id];
    if (!selectedVariants || selectedVariants.length === 0) return;

    const colorValues = product.option_selections
      ?.filter((o: any) => o.name === "Color")
      .flatMap((o: any) => o.values) ?? [];

    const payload = {
      products: [{
        shopify_product_id: product.shopify_product_id,
        option_selections: colorValues.length ? [{
          name: "Color",
          values: colorValues.map((cv: any) => ({
            value: cv.value,
            variant_ids: cv.variant_ids,
            selected: cv.variant_ids.every((v: string) => selectedVariants.includes(v))
          }))
        }] : []
      }]
    };

    const result = await updateProductColors(payload);

    if (result?.success) {
      const updatedColors = result.data?.added?.[0]?.option_selections
        ?.flatMap((o: any) => o.values) ?? [];

      const selectedValuesFromApi = updatedColors
        .filter((c: any) => c.selected === true)
        .map((c: any) => c.value);

      setCollectionProducts(prev => prev.map(p =>
        p.shopify_product_id === product.shopify_product_id
          ? {
              ...p,
              option_selections: [{
                name: "Color",
                values: colorValues.map((cv: any) => ({
                  ...cv,
                  selected: selectedValuesFromApi.includes(cv.value)
                }))
              }]
            }
          : p
      ));
      setProductColorChanges(prev => {
        const next = { ...prev };
        delete next[product.shopify_product_id];
        return next;
      });
      setProductToastMsg(prev => ({ ...prev, [product.shopify_product_id]: "success" }));
    } else {
      const msg = result?.code === 401 || result?.message?.toLowerCase().includes("token")
        ? <>⚠️ Session expired. Please <a href="/login" style={{ color: "#a59cff", textDecoration: "underline" }}>login</a> again.</>
        : (result?.message || "Failed to save colors.");
      setProductsActionError(msg);
      setProductToastMsg(prev => ({ ...prev, [product.shopify_product_id]: "error" }));
    }

    setTimeout(() => {
      setProductToastMsg(prev => {
        const n = { ...prev };
        delete n[product.shopify_product_id];
        return n;
      });
    }, 2500);
  };

  const currentRole = JSON.parse(localStorage.getItem("user") || '{}').current_role;
  const storeName = JSON.parse(localStorage.getItem("user") || '{}')?.stores?.find((s: any) => String(s.id) === String(userStoreId))?.name?.split(' ').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ') ?? "";

  const toTitleCase = (str: string) =>
  str?.split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");


    return (
    <div className="collections-page-wrapper">
      {currentRole != 1 && <DashboardSidebar />}
      <div className="collections-content">
        <div className="div-block-28">

          <div className="collections-toolbar">
            <nav className="collections-breadcrumb">
              {(currentRole==1) ? 
              (<span className="collections-breadcrumb-link" onClick={() => navigate("/admin-dashboard")}>
                {storeName || "Dashboard"}
              </span>) : (<span className="collections-breadcrumb-link" onClick={() => navigate("/dashboard")}>
                {storeName || "Dashboard"}
              </span>)}
              <span className="collections-breadcrumb-sep">›</span>
              <span className="collections-breadcrumb-link" onClick={() => navigate(`/product-groups/${userStoreId}`, { state: { storeName: location.state?.storeName } })}>
                Product Groups
              </span>
              <span className="collections-breadcrumb-sep">›</span>
              <span className="collections-breadcrumb-current">
                {isEditMode ? (toTitleCase(collectionName) || "Edit Group") : "New Group"}
              </span>
            </nav>
          </div>

          <div className="product-selection">
            {(saveMsg || updateErrorContent || createErrorContent) && (
              <div className="save-msg">
                {updateErrorContent || createErrorContent || saveMsg}
              </div>
            )}

            {isProductsMode ? (
              <div className="collection-label-block">
                <h3 className="collection-label-title">{toTitleCase(collectionName)}</h3>
                {description && <p className="collection-label-desc">{description}</p>}
              </div>
            ) : (
              <>
                <div className="w-form">
                  <form id="collection-form" name="collection-form">
                    <label htmlFor="collectionName">Product Group Name</label>
                    <input
                      className="w-input"
                      maxLength={256}
                      name="collectionName"
                      placeholder=""
                      type="text"
                      id="collectionName"
                      value={collectionName}
                      onChange={(e) => { setCollectionName(e.target.value); }}
                    />
                    <label htmlFor="description">Description</label>
                    <textarea
                      placeholder=""
                      maxLength={5000}
                      id="description"
                      name="description"
                      className="w-input"
                      value={description}
                      onChange={(e) => { setDescription(e.target.value); }}
                    />
                  </form>
                  <div className="w-form-done">
                    <div>Thank you! Your submission has been received!</div>
                  </div>
                  <div className="w-form-fail">
                    <div>Oops! Something went wrong while submitting the form.</div>
                  </div>
                </div>
              </>
            )}

            {isProductsMode && (
              <>
                <div className="collection-products-header">
                  <h3 className="collection-title">Products</h3>
                  <button
                    type="button"
                    className="collection-button w-button"
                    onClick={() => setAddProductsOpen(true)}
                  >
                    + Add Products
                  </button>
                </div>

                <AddProductsDialog
                  open={addProductsOpen}
                  onClose={() => setAddProductsOpen(false)}
                  collectionId={savedCollectionId!}
                  alreadyAddedIds={collectionProducts.map(p => String(p.shopify_product_id ?? p.id))}
                  alreadyAddedProducts={collectionProducts.map(p => ({
                    shopify_product_id: String(p.shopify_product_id ?? p.id),
                    shopify_variant_ids: typeof p.shopify_variant_ids === "string"
                      ? JSON.parse(p.shopify_variant_ids || "null")
                      : p.shopify_variant_ids ?? null
                  }))}
                  // onSaved={(newProducts: any[]) => setCollectionProducts(prev => [...prev, ...newProducts])}
                  onSaved={() => fetchProducts()}
                />

                {productsLoading && <p className="collection-empty-msg">Loading products...</p>}
                {!productsLoading && productsErrorContent && (
                  <div className="collection-empty-msg">{productsErrorContent}</div>
                )}
                {productsActionError && (
                  <div className="collection-empty-msg">{productsActionError}</div>
                )}
                {!productsLoading && !productsErrorContent && collectionProducts.length === 0 && (
                  <p className="collection-empty-msg">No products added yet.</p>
                )}

              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext
                  items={collectionProducts.map(p => String(p.id))}
                  strategy={verticalListSortingStrategy}
                >
                {collectionProducts.map((product: any, index: number) => {
                const isActive = product.status === "active";
                const colorOption = {
                  values: product.option_selections
                    ?.filter((o: any) => o.name === "Color")
                    .flatMap((o: any) => o.values) ?? []
                };

                const toggleColorVariant = (variantIds: string[]) => {
                  if (!isActive) return;
                  setProductColorChanges(prev => {
                    const allColorsNull = product.option_selections === null ||
                      (Array.isArray(product.option_selections) && product.option_selections.length === 0);
                    const allColorsFalse = !allColorsNull && colorOption.values.length > 0 &&
                      colorOption.values.every((c: any) => c.selected === false);
                    const isAllSelected = allColorsNull || allColorsFalse;

                    // If all selected state, expand to all variant ids first before toggling
                    const existing = prev[product.shopify_product_id] !== undefined
                      ? prev[product.shopify_product_id]
                      : isAllSelected
                        ? colorOption.values.flatMap((c: any) => c.variant_ids)
                        : colorOption.values.filter((c: any) => c.selected).flatMap((c: any) => c.variant_ids);

                    const allSelected = variantIds.every(v => existing.includes(v));
                    const updated = allSelected
                      ? existing.filter((v: string) => !variantIds.includes(v))
                      : Array.from(new Set([...existing, ...variantIds]));
                    return { ...prev, [product.shopify_product_id]: updated };
                  });
                };

                return (
                <SortableProductRow key={product.id ?? product.shopify_product_id} id={product.id ?? product.shopify_product_id}>
                  {(dragHandle) => (
                  <div className={`product-clc-row product-clc-row--edit${!isActive ? " product-clc-row--disabled" : ""}`}>
                    <div className="product-row-left">
                      {dragHandle}
                      <div className="collection-div product-serial-div">
                        <div className="text-block-3 product-serial-text">{index + 1}</div>
                      </div>
                      <img src={product.product_image_url || ''} loading="lazy" width="Auto" alt={product.product_title} className="image-27" />
                      <div className="collection-div product-title-div">
                        <div className="text-block-3">{`${product.product_title}${
                          product.option_selections
                            ?.find((o: any) => o.name === "Color")?.values?.[0]?.sku
                            ? ` - ${product.option_selections.find((o: any) => o.name === "Color")!.values[0].sku.split("-")[0]}`
                            : ""
                        }`}</div>

                        {colorOption && colorOption.values.length > 0 && (
                          <div className="product-color-list">
                            {colorOption.values.map((cv: any) => {
                              const allColorsNull = product.option_selections === null ||
                                (Array.isArray(product.option_selections) && product.option_selections.length === 0);
                              const allColorsFalse = !allColorsNull && colorOption.values.length > 0 &&
                                colorOption.values.every((c: any) => c.selected === false);
                              const isColorAlreadySaved = allColorsNull || allColorsFalse || cv.selected === true;
                              const pendingSelected = productColorChanges[product.shopify_product_id];
                              const isColorChecked = pendingSelected !== undefined
                              ? cv.variant_ids.every((v: string) => pendingSelected.includes(v))
                              : isColorAlreadySaved;
                              const isDisabled = !isActive;
                              return (
                                <Box
                                  key={cv.value}
                                  onClick={() => !isDisabled && toggleColorVariant(cv.variant_ids)}
                                  sx={{
                                    display: "inline-flex", alignItems: "center",
                                    cursor: isDisabled ? "default" : "pointer",
                                    opacity: isDisabled ? 0.5 : 1,
                                    mr: 1,
                                  }}
                                >
                                  <Checkbox
                                    checked={isColorChecked}
                                    disabled={isDisabled}
                                    size="small"
                                    className="themed-checkbox"
                                    sx={{ p: 0, mr: 0.5 }}
                                    onClick={e => e.stopPropagation()}
                                    onChange={() => !isDisabled && toggleColorVariant(cv.variant_ids)}
                                  />
                                  <Typography variant="body2" color="text.secondary">{cv.value}</Typography>
                                </Box>
                              );
                            })}

                            <button
                              className="collection-button collection-button--color-save w-button"
                              style={{
                                maxWidth: product.shopify_product_id in productColorChanges ? 80 : 0,
                                opacity: product.shopify_product_id in productColorChanges ? 1 : 0,
                                padding: product.shopify_product_id in productColorChanges ? "3px 10px" : 0,
                                pointerEvents: product.shopify_product_id in productColorChanges ? "auto" : "none",
                              }}
                              onClick={() => handleUpdateProductColors(product)}
                            >
                              Save
                            </button>

                            <button
                              className="collection-button collection-button--color-cancel w-button"
                              style={{
                                maxWidth: product.shopify_product_id in productColorChanges ? 80 : 0,
                                opacity: product.shopify_product_id in productColorChanges ? 1 : 0,
                                padding: product.shopify_product_id in productColorChanges ? "3px 10px" : 0,
                                pointerEvents: product.shopify_product_id in productColorChanges ? "auto" : "none",
                              }}
                              onClick={() => handleCancelColorChanges(product.shopify_product_id)}
                            >
                              Cancel
                            </button>

                            {productToastMsg[product.shopify_product_id] && (
                              <span className="product-toast-msg">
                                {productToastMsg[product.shopify_product_id] === "success" ? "Saved!" : "Failed!"}
                              </span>
                            )}
                          </div>
                        )}

                      </div>
                    </div>

                    <div className="product-row-right">

                      <span
                        className="product-status-badge"
                        style={{
                          background: isActive ? "#e6f9f0" : "#fef2f2",
                          color: isActive ? "#1a8a4a" : "#c0392b",
                        }}
                      >
                        {product.status}
                      </span>

                      <div className="text-block-4 product-price-text">{product.price}</div>

                      <span
                        className={`stores-action-link stores-action-link--danger product-remove-btn${removingProductId ? " product-remove-btn--disabled" : ""}`}
                        onClick={(e) => { e.stopPropagation(); isActive && !removingProductId && handleRemoveProduct(product.id); }}
                      >
                        {removingProductId === product.id ? "Removing..." : "Remove"}
                      </span>
                    </div>
                  </div>
                )}
              </SortableProductRow>
            );
            })}
            </SortableContext>
          </DndContext>
          {(hasReordered || sortOrderToast) && (
            <div className="buttons-div">
              {hasReordered && <>
              <button
                className="collection-button w-button save-order-btn"
                onClick={handleSaveOrder}
                disabled={isSavingOrder}
              >
                {isSavingOrder ? "Saving..." : "Save"}
              </button>
              <button
                className="collection-button w-button"
                onClick={handleCancelOrder}
                disabled={isSavingOrder}
              >
                Cancel
              </button>
              </>}
              <div className="sort-order-toast-slot">
                {sortOrderToast && (
                  <span className="product-toast-msg">
                    {sortOrderToast === "success" ? "Saved!" : "Failed!"}
                  </span>
                )}
              </div>
            </div>
          )}
              </>
            )}

            {!isProductsMode && (
              <div className="buttons-div">
                <button onClick={handleSave} disabled={isSaving} className="collection-button w-button">
                  {isSaving ? "Saving..." : "Save"}
                </button>
                <button onClick={() => window.history.back()} className="collection-button w-button">
                  Cancel
                </button>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default EditCollections;
